import { useEffect, useMemo, useState } from "react";

type SupportedSite = {
  id: string;
  display_name: string;
  domains: string[];
};

type SupportedSitesCatalog = {
  object: "list";
  schema_version: "stylome-supported-sites-v1";
  count: number;
  data: SupportedSite[];
};

function normalizedHost(value: string) {
  const candidate = value.trim().toLowerCase();
  if (!candidate) return "";
  try {
    return new URL(
      candidate.includes("://") ? candidate : `https://${candidate}`,
    ).hostname
      .replace(/^www\./, "")
      .replace(/\.$/, "");
  } catch {
    return candidate
      .split(/[/?#]/, 1)[0]
      .replace(/^www\./, "")
      .replace(/:\d+$/, "")
      .replace(/\.$/, "");
  }
}

function exactDomainMatch(host: string, domain: string) {
  return host === domain || host.endsWith(`.${domain}`);
}

export function SupportedSites() {
  const [catalog, setCatalog] = useState<SupportedSitesCatalog | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    let active = true;
    fetch("/supported-sites.json")
      .then((response) => {
        if (!response.ok) throw new Error("catalog unavailable");
        return response.json() as Promise<SupportedSitesCatalog>;
      })
      .then((data) => {
        if (active) setCatalog(data);
      })
      .catch(() => {
        if (active) setLoadError(true);
      });
    return () => {
      active = false;
    };
  }, []);

  const result = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const host = normalizedHost(query);
    if (!catalog || !needle) return { exact: [], related: [] };

    const exact = catalog.data.filter((site) =>
      site.domains.some((domain) => exactDomainMatch(host, domain)),
    );
    const exactIds = new Set(exact.map((site) => site.id));
    const related = catalog.data
      .filter(
        (site) =>
          !exactIds.has(site.id) &&
          (site.display_name.toLowerCase().includes(needle) ||
            site.domains.some(
              (domain) => domain.includes(host) || host.includes(domain),
            )),
      )
      .slice(0, 24);
    return { exact, related };
  }, [catalog, query]);

  const hasQuery = Boolean(query.trim());
  const found = result.exact.length + result.related.length;

  return (
    <div className="supported-sites">
      <section className="site-finder" aria-labelledby="site-finder-title">
        <div className="site-finder-copy">
          <h2 id="site-finder-title">Search supported sites</h2>
          <p>
            Enter a domain or paste a URL. We’ll check it against the
            extension’s current first-class integrations.
          </p>
        </div>
        <div className="site-search-control">
          <label htmlFor="site-search">Site or domain</label>
          <div className="site-search-input">
            <input
              id="site-search"
              type="search"
              inputMode="url"
              autoComplete="url"
              placeholder="404media.co"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              disabled={!catalog}
            />
            {query ? (
              <button type="button" onClick={() => setQuery("")}>
                Clear
              </button>
            ) : null}
          </div>
          <p className="site-catalog-status">
            {loadError
              ? "The current site catalog could not be loaded. Please try again."
              : catalog
                ? `${catalog.count} dedicated integrations in the current extension.`
                : "Loading the current extension catalog…"}
          </p>
        </div>
      </section>

      <section className="site-search-results" aria-live="polite">
        {!hasQuery && catalog ? (
          <>
            <p className="search-result-label">All supported sites</p>
            <div className="site-result-grid">
              {catalog.data.map((site) => (
                <SiteResult key={site.id} site={site} variant="browse" />
              ))}
            </div>
          </>
        ) : result.exact.length ? (
          <>
            <p className="search-result-label">Supported</p>
            <div className="site-result-grid">
              {result.exact.map((site) => (
                <SiteResult key={site.id} site={site} variant="exact" />
              ))}
            </div>
          </>
        ) : found ? (
          <>
            <p className="search-result-label">Related matches</p>
            <div className="site-result-grid">
              {result.related.map((site) => (
                <SiteResult key={site.id} site={site} />
              ))}
            </div>
          </>
        ) : catalog ? (
          <div className="site-not-found">
            <span className="badge">No dedicated integration found</span>
            <h2>We don’t list {normalizedHost(query) || query} yet.</h2>
            <p>
              This only checks automatic, site-specific support. You can still
              scan copied text in Stylome’s free paste box.
            </p>
          </div>
        ) : null}
      </section>
    </div>
  );
}

function SiteResult({
  site,
  variant = "related",
}: {
  site: SupportedSite;
  variant?: "browse" | "exact" | "related";
}) {
  const exact = variant === "exact";
  return (
    <article className={`site-result ${variant}`}>
      {variant !== "browse" ? (
        <span className={exact ? "badge badge-good" : "badge"}>
          {exact ? "Supported" : "Possible match"}
        </span>
      ) : null}
      <h3>{site.display_name}</h3>
      <p>{site.domains.join(" · ")}</p>
    </article>
  );
}
